import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://shifttracker.net";
// Opaque 1200x630 JPEG for social previews. Transparent PNGs and large files
// (logo.png is 1024x1024 RGBA, ~1.4MB) fail to render on WhatsApp/Twitter.
const OG_IMAGE_URL = `${BASE_URL}/og-image.jpg`;

type Lang = "tr" | "en";

interface SeoContent {
  title: string;
  description: string;
}

// Language-neutral path ("/", "/about", "/contact") -> per-language meta.
const SEO_CONTENT: Record<string, Record<Lang, SeoContent>> = {
  "/": {
    tr: {
      title:
        "Akaryakıt İstasyonu Yazılımı | Benzin İstasyonu Nakit Akış Yönetimi - Shift Tracker",
      description:
        "Shift Tracker ile vardiya alma bir dakikanın altına iner: banka mutabakatı ve kasa açığı tespiti otomatiktir. Kart satışları banka ödemeleriyle eşleştirilir, nakit yatırmalar gerçek banka ekstrenizle aynı gün doğrulanır; siz yalnızca gözden geçirip vardiyayı kilitlersiniz.",
    },
    en: {
      title:
        "Gas Station Software | Fuel Station Cash Flow Management - Shift Tracker",
      description:
        "Shift Tracker closes a fuel or gas station shift in under a minute: bank reconciliation and deficit detection are automatic. Card sales are matched to your bank payments and cash deposits verified same-day against your real bank statement; you just review and lock.",
    },
  },
  "/about": {
    tr: {
      title:
        "Hakkımızda | İstasyoncular Tarafından Geliştirildi - Shift Tracker",
      description:
        "Shift Tracker'ı neden ve nasıl geliştirdiğimiz: kendi akaryakıt istasyonumuz için kurduğumuz, banka onaylı vardiya mutabakatını bir dakikaya indiren yazılım. Güvene değil, kanıta dayalı istasyon yönetimi.",
    },
    en: {
      title: "About | Built by Fuel-Station Operators - Shift Tracker",
      description:
        "Why and how we built Shift Tracker: software we built for our own fuel station that turns hours of shift reconciliation into a one-minute, bank-verified close. Station management based on proof, not trust.",
    },
  },
  "/contact": {
    tr: {
      title: "İletişim | Akaryakıt İstasyonu Yazılımı Talebi - Shift Tracker",
      description:
        "Akaryakıt ve benzin istasyonu nakit akış yönetimi yazılımı Shift Tracker hakkında bilgi almak ve demo talep etmek için bizimle iletişime geçin.",
    },
    en: {
      title: "Contact | Request Fuel Station Software Demo - Shift Tracker",
      description:
        "Get in touch to learn more and request a demo of Shift Tracker, the cash flow management software for fuel and gas stations.",
    },
  },
  "/fraud-prevention": {
    tr: {
      title:
        "Akaryakıt İstasyonu Dolandırıcılık Önleme ve Denetim | Shift Tracker",
      description:
        "Mükerrer dekont, kart satışının nakit gösterilmesi ve ön ödemeli kart manipülasyonu gibi akaryakıt istasyonu dolandırıcılık açıklarını banka onaylı mutabakat ve denetim izi ile kapatın.",
    },
    en: {
      title: "Gas Station Fraud Detection & Prevention | Shift Tracker",
      description:
        "Close fuel station fraud gaps, duplicate receipts, card sales disguised as cash, and prepaid card manipulation, with bank-proven reconciliation, one-time allocation, and a full audit trail.",
    },
  },
  "/fiyatlandirma": {
    tr: {
      title:
        "Fiyatlandırma | Akaryakıt İstasyonu Yazılımı Fiyatları - Shift Tracker",
      description:
        "Shift Tracker fiyatları: yeni müşterilere özel lansman fiyatıyla aylık 5.000 ₺, yıllık 60.000 ₺. TURPAK entegrasyonu, banka onaylı mutabakat ve canlı istasyon takibi dahil: tek plan, her şey dahil.",
    },
    en: {
      title: "Pricing | Gas Station Software Plans - Shift Tracker",
      description:
        "Shift Tracker pricing: launch price for new customers at ₺5,000/month or ₺60,000/year. TURPAK integration, bank-verified reconciliation and live station tracking included: one plan, everything included.",
    },
  },
};

// All canonical/hreflang URLs use a trailing slash to match what Netlify
// actually serves (react-snap emits directory-index files, so `/about`
// 301-redirects to `/about/`). Keeping these in sync avoids "Page with
// redirect" in Search Console and canonical-vs-served-URL mismatches.
// `path` is the Turkish (root) path; `enPath` is the English path when its slug
// differs (e.g. tr "/akaryakit-istasyonu-yazilimi" ↔ en "/gas-station-software").
// When omitted, the English page uses the same slug under /en/.
function languageUrls(path: string, enPath?: string): { tr: string; en: string } {
  const enP = enPath ?? path;
  const tr = path === "/" ? `${BASE_URL}/` : `${BASE_URL}${path}/`;
  const en = enP === "/" ? `${BASE_URL}/en/` : `${BASE_URL}/en${enP}/`;
  return { tr, en };
}

interface SeoProps {
  // Turkish (root) path of the page, e.g. "/", "/about", "/blog/<tr-slug>".
  path: string;
  // English path when its slug differs from the Turkish one (e.g.
  // "/gas-station-software" or "/blog/<en-slug>"). Defaults to `path`.
  enPath?: string;
  // Optional overrides for pages not in SEO_CONTENT (e.g. blog posts).
  title?: string;
  description?: string;
  // og:type, "website" (default) or "article" for blog posts.
  type?: "website" | "article";
  // Extra JSON-LD payload (e.g. a BlogPosting) emitted alongside the page.
  schema?: object | null;
}

function Seo({
  path,
  enPath,
  title,
  description,
  type = "website",
  schema = null,
}: SeoProps): JSX.Element {
  const location = useLocation();
  const isEnglish =
    location.pathname === "/en" || location.pathname.startsWith("/en/");
  const lang: Lang = isEnglish ? "en" : "tr";

  const fallback = SEO_CONTENT[path]?.[lang];
  const content: SeoContent = {
    title: title ?? fallback?.title ?? "Shift Tracker",
    description: description ?? fallback?.description ?? "",
  };
  const { tr: trUrl, en: enUrl } = languageUrls(path, enPath);
  const canonical = lang === "en" ? enUrl : trUrl;
  const homeUrl = lang === "en" ? `${BASE_URL}/en/` : `${BASE_URL}/`;

  // Breadcrumb trail for non-home pages (Home > Current page).
  const breadcrumbSchema =
    path === "/"
      ? null
      : {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: lang === "en" ? "Home" : "Ana Sayfa",
              item: homeUrl,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: content.title,
              item: canonical,
            },
          ],
        };

  return (
    <Helmet defer={false}>
      <html lang={lang} />
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <link rel="canonical" href={canonical} />

      <link rel="alternate" hrefLang="tr" href={trUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={trUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Shift Tracker" />
      <meta
        property="og:locale"
        content={lang === "en" ? "en_US" : "tr_TR"}
      />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={content.title} />
      <meta property="og:description" content={content.description} />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta property="og:image:secure_url" content={OG_IMAGE_URL} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Shift Tracker" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={content.title} />
      <meta name="twitter:description" content={content.description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />
      <meta name="twitter:image:alt" content="Shift Tracker" />

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

export default Seo;
